import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "./firebase/firebase";

const DOCUMENTS = [
  { label: "Passport", fileKeys: ["passportFile", "passportFileBack"], expiryKey: "passportExpiry" },
  { label: "Emirates ID", fileKeys: ["emiratesIdFile", "emiratesIdFileBack"], expiryKey: "emiratesIdExpiry" },
  { label: "Driving License", fileKeys: ["licenseFile", "licenseFileBack"], expiryKey: "licenseExpiry" },
  { label: "RTA Permit", fileKeys: ["rtaPermitFile"], expiryKey: "rtaPermitExpiry" },
  { label: "RTA Training", fileKeys: ["rtaTrainingFile"], expiryKey: "rtaTrainingExpiry" },
  { label: "Health Insurance", fileKeys: ["healthInsuranceFile"], expiryKey: "healthInsuranceExpiry" },
];

function toDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  if (value?.seconds) return new Date(value.seconds * 1000);
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.slice(0, 10))) {
    return new Date(`${value.slice(0, 10)}T12:00:00`);
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isActiveEmployee(driver) {
  return !["cancelled", "inactive", "terminated"].includes(
    String(driver?.status || "").trim().toLowerCase()
  );
}

function hasAllFiles(driver, docDef) {
  return docDef.fileKeys.every((key) => Boolean(driver?.[key]));
}

const StatCard = ({ title, value, note, gradient }) => (
  <div style={{ borderRadius: 18, padding: 20, background: gradient, color: "white", boxShadow: "0 10px 28px rgba(15,23,42,0.14)" }}>
    <div style={{ fontSize: 13, fontWeight: 800, opacity: 0.88 }}>{title}</div>
    <div style={{ fontSize: 38, fontWeight: 900, marginTop: 8 }}>{value}</div>
    <div style={{ fontSize: 12, opacity: 0.84, marginTop: 6 }}>{note}</div>
  </div>
);

export default function DashboardDocsLink() {
  const navigate = useNavigate();
  const email = localStorage.getItem("email");
  const [drivers, setDrivers] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "drivers"),
      (snap) => setDrivers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        console.error("Document dashboard employees error:", err);
        setError("Document dashboard could not load employees. Please check the published Firestore rules.");
      }
    );
    return () => unsub();
  }, []);

  const dashboard = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const thirtyDays = new Date(now);
    thirtyDays.setDate(thirtyDays.getDate() + 30);
    const sixtyDays = new Date(now);
    sixtyDays.setDate(sixtyDays.getDate() + 60);

    const activeDrivers = drivers.filter(isActiveEmployee);
    let expiredDocuments = 0;
    let expiring30 = 0;
    let expiring60 = 0;
    let missingDocuments = 0;
    const attentionDrivers = new Set();
    const docBreakdown = DOCUMENTS.map((docDef) => ({ label: docDef.label, expired: 0, near: 0, missing: 0 }));

    activeDrivers.forEach((driver) => {
      DOCUMENTS.forEach((docDef, index) => {
        if (!hasAllFiles(driver, docDef)) {
          missingDocuments += 1;
          docBreakdown[index].missing += 1;
          attentionDrivers.add(driver.id);
        }

        const expiry = toDate(driver[docDef.expiryKey]);
        if (!expiry) return;
        expiry.setHours(0, 0, 0, 0);
        if (expiry < now) {
          expiredDocuments += 1;
          docBreakdown[index].expired += 1;
          attentionDrivers.add(driver.id);
        } else if (expiry <= thirtyDays) {
          expiring30 += 1;
          docBreakdown[index].near += 1;
          attentionDrivers.add(driver.id);
        } else if (expiry <= sixtyDays) {
          expiring60 += 1;
        }
      });
    });

    return {
      activeEmployees: activeDrivers.length,
      expiredDocuments,
      expiring30,
      expiring60,
      missingDocuments,
      attentionDrivers: attentionDrivers.size,
      docBreakdown,
    };
  }, [drivers]);

  return (
    <div style={{ padding: 30, maxWidth: 1280, margin: "0 auto" }}>
      <div style={{ borderRadius: 24, padding: "30px 32px", background: "linear-gradient(135deg,#312e81,#6d28d9,#9333ea)", color: "white", boxShadow: "0 16px 40px rgba(76,29,149,0.24)" }}>
        <div style={{ fontSize: 13, fontWeight: 800, opacity: 0.8 }}>EMPLOYEE DOCUMENT CONTROL</div>
        <h1 style={{ margin: "7px 0 5px", fontSize: 31 }}>Documents Dashboard</h1>
        <div style={{ opacity: 0.88 }}>Welcome, {email}</div>
        <div style={{ marginTop: 11, fontSize: 13, opacity: 0.82 }}>
          Upload documents and expiry dates directly to the employee record. No driver upload link and no approval step are required.
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 20 }}>
          <button onClick={() => navigate("/drivers")} style={buttonStyle("white", "#4c1d95")}>Manage Driver Documents</button>
        </div>
      </div>

      {error ? <div style={{ marginTop: 16, padding: 12, borderRadius: 10, background: "#fff7ed", color: "#9a3412", fontWeight: 700 }}>{error}</div> : null}

      <div style={{ display: "grid", gap: 18, gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", marginTop: 22 }}>
        <StatCard title="Active Employees" value={dashboard.activeEmployees} note="Cancelled/inactive employees excluded" gradient="linear-gradient(135deg,#2563eb,#06b6d4)" />
        <StatCard title="Employees Needing Attention" value={dashboard.attentionDrivers} note="Missing, expired or near-expiry documents" gradient="linear-gradient(135deg,#7c3aed,#a855f7)" />
        <StatCard title="Expired Documents" value={dashboard.expiredDocuments} note="Needs immediate document update" gradient="linear-gradient(135deg,#b91c1c,#ef4444)" />
        <StatCard title="Expiring in 30 Days" value={dashboard.expiring30} note={`${dashboard.expiring60} more expire within 31–60 days`} gradient="linear-gradient(135deg,#c2410c,#f59e0b)" />
        <StatCard title="Missing Document Sets" value={dashboard.missingDocuments} note="A required file/page is missing" gradient="linear-gradient(135deg,#475569,#64748b)" />
      </div>

      <section style={{ ...panelStyle, marginTop: 22 }}>
        <h3 style={headingStyle}>Document Risk by Type</h3>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 620 }}>
            <thead>
              <tr style={{ background: "#f8fafc", color: "#475569", fontSize: 12 }}>
                <th style={thStyle}>Document</th>
                <th style={thStyle}>Expired</th>
                <th style={thStyle}>Expiring ≤30 days</th>
                <th style={thStyle}>Missing files/pages</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.docBreakdown.map((row) => (
                <tr key={row.label} style={{ borderTop: "1px solid #e5e7eb" }}>
                  <td style={{ ...tdStyle, fontWeight: 800 }}>{row.label}</td>
                  <td style={{ ...tdStyle, color: row.expired ? "#b91c1c" : "#334155", fontWeight: 800 }}>{row.expired}</td>
                  <td style={{ ...tdStyle, color: row.near ? "#c2410c" : "#334155", fontWeight: 800 }}>{row.near}</td>
                  <td style={{ ...tdStyle, color: row.missing ? "#7c2d12" : "#334155", fontWeight: 800 }}>{row.missing}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function buttonStyle(background, color) {
  return { border: "1px solid rgba(255,255,255,0.35)", borderRadius: 10, padding: "10px 14px", background, color, fontWeight: 900, cursor: "pointer" };
}

const panelStyle = { background: "white", border: "1px solid #e5e7eb", borderRadius: 17, padding: 20, boxShadow: "0 6px 18px rgba(15,23,42,0.05)" };
const headingStyle = { margin: "0 0 12px", fontSize: 18, color: "#0f172a" };
const thStyle = { textAlign: "left", padding: "11px 12px" };
const tdStyle = { padding: "12px", color: "#334155" };
